/* eslint-disable max-len */
import { DownloadOutlined } from '@ant-design/icons';
import { message, Select } from 'antd';
import * as React from 'react';
import { HocOptions } from '../common/generic-hoc.types';
import { getServiceList, getVirtualInventory } from '../../network/virtual-inventory';
import { StylesProps, ThemeType } from '../../theme/jss-types';
import { ExcelColumn, Master } from '../../types/master-data-types';
import { VirtualInventoryColumns } from './virtual-inventory.constants';
import moment from 'moment';
import { IdName, Inventory } from '../../library/Types';
import GenericHoc from '../common/generic-hoc';
import { NAVBAR_HEIGHT } from 'library/globals';
import { useTranslation } from 'react-i18next';
import { getCurrentCustomer } from 'components/common/utils';
import FixedHeaderTable from 'components/common/FixedHeaderTable/FixedHeaderTable';

const styles = (theme: ThemeType) => ({
    main: {
        width: '100%',
        padding: '5px 6px 6px 3px',
        height: '100%',
    },
    filters: {
        display: 'flex',
        flexDirection: 'column',
    },
    row: {
        display: 'flex',
        alignItems: 'center',
    },
    extraFilters: {
        width: '100%',
        borderRadius: '0 2px 0 0',
        backgroundColor: '#FFFFFF',
        padding: '8px 16px',
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: 4,
        flexDirection: 'row',
    },
    cellValue: {
        color: '#111111',
        fontFamily: 'Open Sans',
        fontSize: 12,
        fontWeight: 600,
        letterSpacing: 0,
    },
    cellNa: {
        color: '#999999',
        fontFamily: 'Open Sans',
        fontSize: 12,
        fontWeight: 600,
        letterSpacing: 0,
    },
    filter: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        marginRight: 12,
        fontFamily: 'Open Sans',
    },
    filterText: {
        color: '#333333',
        fontFamily: 'Open Sans',
        fontSize: 12,
        letterSpacing: 0,
        marginRight: 8,
    },
    selectFilter: {
        marginRight: '12px !important',
        width: '220px',
        '& .ant-select:not(.ant-select-customize-input) .ant-select-selector': {
            backgroundColor: '#fafafa',
        },
        '& .ant-select-selector': {
            width: 'auto !important',
            borderRadius: '6px !important',
            alignItems: 'center',
        },
        '& .ant-select-dropdown': {
            padding: '12px !important',
            borderRadius: '6px !important',
            gap: '16px !important',
        },
        '& .ant-select-item': {
            padding: '8px !important',
            borderRadius: '4px !important',
            gap: '10px !important',
            fontWeight: 600,
        },
        '& .ant-select-item-option-selected': {
            backgroundColor: `${theme.tertiaryFillColor} !important`,
            padding: '8px !important',
            borderRadius: '4px !important',
            gap: '10px !important',
        },
    },
});

interface VirtualInventoryProps extends StylesProps<ReturnType<typeof styles>> {
    master: Master,
}

const VirtualInventory = (props: VirtualInventoryProps) => {
    const { classes, master } = props;
    const [virtualList, setVirtualList] = React.useState<Inventory[]>([]);
    const [loading, setLoading] = React.useState<boolean>(true);
    const [serviceType, setServiceType] = React.useState<string[]>();
    const [services, setServices] = React.useState<IdName[]>([]);
    const [childClients, setChildClients] = React.useState<any>([]);
    const [selectedCustomer, setSelectedCustomer] = React.useState<any>(window.localStorage.getItem('childCustomerId'));
    const [selectedClientCode, setSelectedClientCode] = React.useState<any>(window.localStorage.getItem('childCustomerCode'));
    const bannerHeight = (master?.config?.customer_portal_config?.password_policy_banner_expiry_date
        && new Date(master?.config?.customer_portal_config?.password_policy_banner_expiry_date) <= new Date()) ? 0 : 50;
    const { t } = useTranslation();

    const allow_child_login_on_parent = master?.parts_to_show?.allow_child_login_on_parent || false;

    const loadData = async (id: any) => {
        setLoading(true);
        const response = await getVirtualInventory({
            serviceType,
            clientId: id || window.localStorage.getItem('childCustomerId'),
        });
        if (response.isSuccess) {
            setVirtualList(response?.data || []);
        } else {
            message.error(response.errorMessage);
        }
        setLoading(false);
    };

    const loadServices = async () => {
        const response = await getServiceList({
        });
        setServices(response?.data || []);
    };

    const loadChildData = async () => {
        const result = getCurrentCustomer(master?.children_list);
        setChildClients(result || []);
        const customer = result?.length ? result[0].customer_id
            : window.localStorage.getItem('childCustomerId');
        setSelectedCustomer(customer);
    };

    React.useEffect(() => {
        loadServices();
    }, []);

    React.useEffect(() => {
        loadData(selectedCustomer);
    }, []);

    React.useEffect(() => {
        loadChildData();
    }, []);

    const renderFilter = () => (
        <div key="serviceType">
            <Select
                className={classes.selectFilter}
                value={serviceType}
                allowClear
                placeholder={t('select_service_type')}
                onChange={setServiceType}
                dropdownMatchSelectWidth={false}
                mode="tags"
                maxTagCount="responsive"
                getPopupContainer={(triggerNode) => triggerNode.parentNode}
            >
                {services.map((service) => (
                    <Select.Option key={service.name} value={service.name}>
                        {service.name}
                    </Select.Option>
                ))}
            </Select>
        </div>
    );

    const handleCustomerChange = (val: any, options: any) => {
        if (options?.label) {
            setSelectedClientCode(options.label);
        }
        loadData(val);
    };

    const renderChildDropdown = () => {
        if (!allow_child_login_on_parent) {
            return null;
        }
        const childList = childClients?.map((item: any) => {
            return {
                value: item.customer_id,
                label: item.customer_code,
            };
        });
        return (
            <div className={classes.filter}>
                <span className={classes.filterText}>
                    Entity Code
                </span>
                <Select
                    className={classes.selectFilter}
                    placeholder="Select Account"
                    dropdownMatchSelectWidth={false}
                    defaultValue={selectedCustomer}
                    onChange={handleCustomerChange}
                    showSearch
                    allowClear
                    options={childList}
                    filterOption={(query, option: any) => {
                        return option?.label?.toUpperCase()?.includes(query.toUpperCase());
                    }}
                    getPopupContainer={(triggerNode) => triggerNode.parentNode}
                />
            </div>
        );
    };
    const renderExtraFilters = () => {
        return (
            <div className={classes.extraFilters}>
                {renderFilter()}
                {renderChildDropdown()}
            </div>
        );
    };

    const renderText = (text: string) => {
        return (
            <div
                className={classes.cellValue}
            >
                {text}
            </div>
        );
    };

    const renderColumn = (text: any, column: ExcelColumn) => {
        if (column.column_id === 'action') {
            return (
                <DownloadOutlined />
            );
        }
        if (column.column_id === 'createdAt') {
            return renderText(moment(text).format('DD MMM, YYYY'));
        }
        if (column.column_id === 'serviceType') {
            return renderText(text.join(', '));
        }
        if (column.column_id === 'clientId') {
            let userCode = window.localStorage.getItem('userCode') || '';
            if (allow_child_login_on_parent) {
                userCode = selectedClientCode || '';
            }
            return renderText(userCode);
        }
        if (column.column_id === 'type') {
            return renderText(text?.toUpperCase());
        }
        return renderText(text);
    };

    const getColumns = (): any[] => {
        const columns: any = VirtualInventoryColumns.map((column: ExcelColumn) => {
            return {
                key: column.column_id,
                title: t(column.pretty_name),
                dataIndex: column.column_id,
                ellipsis: true,
                bordered: false,
                width: column.width,
                render: (text: string) => renderColumn(text, column),
            };
        });
        return columns;
    };

    const renderTable = () => {
        let filteredList = virtualList;
        if (serviceType && serviceType.length) {
            filteredList = virtualList.filter((item) => {
                let isAvailable = false;
                serviceType.forEach((service) => {
                    isAvailable = isAvailable || item.serviceType.includes(service);
                });
                return isAvailable;
            });
        }
        return (
            <FixedHeaderTable
                bordered={false}
                loading={loading}
                pagination={false}
                rowKey={(row: any) => row.id}
                columns={getColumns()}
                dataSource={filteredList}
                scroll={{
                    y: `calc(((100vh - ${NAVBAR_HEIGHT}px) - 115px) - ${bannerHeight}px)`,
                }}
                style={{
                    height: `calc(
                        (((100vh - ${NAVBAR_HEIGHT}px) - 55px) - ${bannerHeight}px) )`,
                    background: '#ffffff',
                }}
            />
        );
    };

    return (
        <div className={classes.main}>
            {renderExtraFilters()}
            {renderTable()}
        </div>
    );
};

const mapStateToProps = (state: any) => {
    return {
        master: state.master,
    };
};

const hocConfig: HocOptions = {
    connectRedux: {
        useRedux: true,
        mapStateToProps,
    },
    connectJss: {
        useJss: true,
        styleSheet: styles,
    },
    connectRouter: true,
    connectTranslession: true,
};

export default GenericHoc(hocConfig)(VirtualInventory);
